/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */


import {coerceBooleanProperty} from '@angular/cdk/coercion';
import {DOWN_ARROW} from '@angular/cdk/keycodes';
import {
  AfterContentInit,
  Directive,
  ElementRef,
  EventEmitter,
  forwardRef,
  Inject,
  Input,
  OnDestroy,
  Optional,
  Output,
  Renderer2,
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import {DateAdapter, MD_DATE_FORMATS, MdDateFormats} from '@angular/material/core';
import {MdFormField} from '@angular/material/form-field';
import {Subscription} from 'rxjs/Subscription';
import {coerceDateProperty} from './coerce-date-property';
import {MdDatepicker} from './datepicker';
import {createMissingDateImplError} from './datepicker-errors';


export const /** @type {?} */ MD_DATEPICKER_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => MdDatepickerInput),
  multi: true
};


export const /** @type {?} */ MD_DATEPICKER_VALIDATORS: any = {
  provide: NG_VALIDATORS,
  useExisting: forwardRef(() => MdDatepickerInput),
  multi: true
};
/**
 * An event used for datepicker input and change events. We don't always have access to a native
 * input or change event because the event may have been triggered by the user clicking on the
 * calendar popup. For consistency, we always use MdDatepickerInputEvent instead.
 */
export class MdDatepickerInputEvent<D> {
/**
 * The new value for the target datepicker input.
 */
value: D | null;
/**
 * @param {?} target
 * @param {?} targetElement
 */
constructor(public target: MdDatepickerInput<D>,
public targetElement: HTMLElement) {
    this.value = this.target.value;
  }
}

function MdDatepickerInputEvent_tsickle_Closure_declarations() {
/**
 * The new value for the target datepicker input.
 * @type {?}
 */
MdDatepickerInputEvent.prototype.value;
/** @type {?} */
MdDatepickerInputEvent.prototype.target;
/** @type {?} */
MdDatepickerInputEvent.prototype.targetElement;
}

/**
 * Directive used to connect an input to a MdDatepicker.
 */
export class MdDatepickerInput<D> implements AfterContentInit, ControlValueAccessor, OnDestroy,
    Validator {
/**
 * The datepicker that this input is associated with.
 * @param {?} value
 * @return {?}
 */
set mdDatepicker(value: MdDatepicker<D>) {
    this.registerDatepicker(value);
  }
  _datepicker: MdDatepicker<D>;
/**
 * @param {?} value
 * @return {?}
 */
private registerDatepicker(value: MdDatepicker<D>) {
    if (value) {
      this._datepicker = value;
      this._datepicker._registerInput(this);
    }
  }
/**
 * @param {?} value
 * @return {?}
 */
set matDatepicker(value: MdDatepicker<D>) {
    // Note that we don't set `this.mdDatepicker = value` here,
    // because that line gets stripped by the JS compiler.
    this.registerDatepicker(value);
  }
/**
 * @param {?} filter
 * @return {?}
 */
set mdDatepickerFilter(filter: (date: D | null) => boolean) {
    this._dateFilter = filter;
    this._validatorOnChange();
  }
  _dateFilter: (date: D | null) => boolean;
/**
 * @param {?} filter
 * @return {?}
 */
set matDatepickerFilter(filter: (date: D | null) => boolean) {
    this.mdDatepickerFilter = filter;
  }
/**
 * The value of the input.
 * @return {?}
 */
get value(): D | null {
    return this._value;
  }
/**
 * @param {?} value
 * @return {?}
 */
set value(value: D | null) {
    value = coerceDateProperty(this._dateAdapter, value);
    this._lastValueValid = !value || this._dateAdapter.isValid(value);
    value = this._getValidDateOrNull(value);

    let /** @type {?} */ oldDate = this.value;
    this._value = value;
    this._renderer.setProperty(this._elementRef.nativeElement, 'value',
        value ? this._dateAdapter.format(value, this._dateFormats.display.dateInput) : '');
    if (!this._dateAdapter.sameDate(oldDate, value)) {
      this._valueChange.emit(value);
    }
  }
private _value: D | null;
/**
 * The minimum valid date.
 * @return {?}
 */
get min(): D | null { return this._min; }
/**
 * @param {?} value
 * @return {?}
 */
set min(value: D | null) {
    this._min = coerceDateProperty(this._dateAdapter, value);
    this._validatorOnChange();
  }
private _min: D | null;
/**
 * The maximum valid date.
 * @return {?}
 */
get max(): D | null { return this._max; }
/**
 * @param {?} value
 * @return {?}
 */
set max(value: D | null) {
    this._max = coerceDateProperty(this._dateAdapter, value);
    this._validatorOnChange();
  }
private _max: D | null;
/**
 * Whether the datepicker-input is disabled.
 * @return {?}
 */
get disabled() { return this._disabled; }
/**
 * @param {?} value
 * @return {?}
 */
set disabled(value: any) {
    const /** @type {?} */ newValue = coerceBooleanProperty(value);

    if (this._disabled !== newValue) {
      this._disabled = newValue;
      this._disabledChange.emit(newValue);
    }
  }
private _disabled: boolean;
/**
 * Emits when a `change` event is fired on this `<input>`.
 */
dateChange = new EventEmitter<MdDatepickerInputEvent<D>>();
/**
 * Emits when an `input` event is fired on this `<input>`.
 */
dateInput = new EventEmitter<MdDatepickerInputEvent<D>>();
/**
 * Emits when the value changes (either due to user input or programmatic change).
 */
_valueChange = new EventEmitter<D|null>();
/**
 * Emits when the disabled state has changed
 */
_disabledChange = new EventEmitter<boolean>();

  _onTouched = () => {};
private _cvaOnChange: (value: any) => void = () => {};
private _validatorOnChange = () => {};
private _datepickerSubscription = Subscription.EMPTY;
private _localeSubscription = Subscription.EMPTY;
/**
 * The form control validator for whether the input parses.
 */
private _parseValidator: ValidatorFn = (): ValidationErrors | null => {
    return this._lastValueValid ?
        null : {'mdDatepickerParse': {'text': this._elementRef.nativeElement.value}};
  }
/**
 * The form control validator for the min date.
 */
private _minValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    const controlValue = coerceDateProperty(this._dateAdapter, control.value);
    return (!this.min || !controlValue ||
        this._dateAdapter.compareDate(this.min, controlValue) <= 0) ?
        null : {'mdDatepickerMin': {'min': this.min, 'actual': controlValue}};
  }
/**
 * The form control validator for the max date.
 */
private _maxValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    const controlValue = coerceDateProperty(this._dateAdapter, control.value);
    return (!this.max || !controlValue ||
        this._dateAdapter.compareDate(this.max, controlValue) >= 0) ?
        null : {'mdDatepickerMax': {'max': this.max, 'actual': controlValue}};
  }
/**
 * The form control validator for the date filter.
 */
private _filterValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    const controlValue = coerceDateProperty(this._dateAdapter, control.value);
    return !this._dateFilter || !controlValue || this._dateFilter(controlValue) ?
        null : {'mdDatepickerFilter': true};
  }
/**
 * The combined form control validator for this input.
 */
private _validator: ValidatorFn | null =
      Validators.compose(
          [this._parseValidator, this._minValidator, this._maxValidator, this._filterValidator]);
/**
 * Whether the last value set on the input was valid.
 */
private _lastValueValid = false;
/**
 * @param {?} _elementRef
 * @param {?} _renderer
 * @param {?} _dateAdapter
 * @param {?} _dateFormats
 * @param {?} _mdFormField
 */
constructor(
private _elementRef: ElementRef,
private _renderer: Renderer2,
private _dateAdapter: DateAdapter<D>,
private _dateFormats: MdDateFormats,
private _mdFormField: MdFormField) {
    if (!this._dateAdapter) {
      throw createMissingDateImplError('DateAdapter');
    }
    if (!this._dateFormats) {
      throw createMissingDateImplError('MD_DATE_FORMATS');
    }

    // Update the displayed date when the locale changes.
    this._localeSubscription = _dateAdapter.localeChanges.subscribe(() => {
      this.value = this.value;
    });
  }
/**
 * @return {?}
 */
ngAfterContentInit() {
    if (this._datepicker) {
      this._datepickerSubscription =
          this._datepicker.selectedChanged.subscribe((selected: D) => {
            this.value = selected;
            this._cvaOnChange(selected);
            this._onTouched();
            this.dateInput.emit(new MdDatepickerInputEvent(this, this._elementRef.nativeElement));
            this.dateChange.emit(new MdDatepickerInputEvent(this, this._elementRef.nativeElement));
          });
    }
  }
/**
 * @return {?}
 */
ngOnDestroy() {
    this._datepickerSubscription.unsubscribe();
    this._localeSubscription.unsubscribe();
    this._valueChange.complete();
    this._disabledChange.complete();
  }
/**
 * @param {?} fn
 * @return {?}
 */
registerOnValidatorChange(fn: () => void): void {
    this._validatorOnChange = fn;
  }
/**
 * @param {?} c
 * @return {?}
 */
validate(c: AbstractControl): ValidationErrors | null {
    return this._validator ? this._validator(c) : null;
  }
/**
 * Gets the element that the datepicker popup should be connected to.
 * @return {?} The element to connect the popup to.
 */
getPopupConnectionElementRef(): ElementRef {
    return this._mdFormField ? this._mdFormField.underlineRef : this._elementRef;
  }
/**
 * @param {?} value
 * @return {?}
 */
writeValue(value: D): void {
    this.value = value;
  }
/**
 * @param {?} fn
 * @return {?}
 */
registerOnChange(fn: (value: any) => void): void {
    this._cvaOnChange = fn;
  }
/**
 * @param {?} fn
 * @return {?}
 */
registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }
/**
 * @param {?} disabled
 * @return {?}
 */
setDisabledState(disabled: boolean): void {
    this._renderer.setProperty(this._elementRef.nativeElement, 'disabled', disabled);
  }
/**
 * @param {?} event
 * @return {?}
 */
_onKeydown(event: KeyboardEvent) {
    if (event.altKey && event.keyCode === DOWN_ARROW) {
      this._datepicker.open();
      event.preventDefault();
    }
  }
/**
 * @param {?} value
 * @return {?}
 */
_onInput(value: string) {
    let /** @type {?} */ date = this._dateAdapter.parse(value, this._dateFormats.parse.dateInput);
    this._lastValueValid = !date || this._dateAdapter.isValid(date);
    date = this._getValidDateOrNull(date);
    this._value = date;
    this._cvaOnChange(date);
    this._valueChange.emit(date);
    this.dateInput.emit(new MdDatepickerInputEvent(this, this._elementRef.nativeElement));
  }
/**
 * @return {?}
 */
_onChange() {
    this.dateChange.emit(new MdDatepickerInputEvent(this, this._elementRef.nativeElement));
  }
/**
 * @param {?} obj The object to check.
 * @return {?} The given object if it is both a date instance and valid, otherwise null.
 */
private _getValidDateOrNull(obj: any): D | null {
    return (this._dateAdapter.isDateInstance(obj) && this._dateAdapter.isValid(obj)) ? obj : null;
  }
static decorators: DecoratorInvocation[] = [
{ type: Directive, args: [{
  selector: 'input[mdDatepicker], input[matDatepicker]',
  providers: [MD_DATEPICKER_VALUE_ACCESSOR, MD_DATEPICKER_VALIDATORS],
  host: {
    '[attr.aria-haspopup]': 'true',
    '[attr.aria-owns]': '(_datepicker?.opened && _datepicker.id) || null',
    '[attr.min]': 'min ? _dateAdapter.toIso8601(min) : null',
    '[attr.max]': 'max ? _dateAdapter.toIso8601(max) : null',
    '[disabled]': 'disabled',
    '(input)': '_onInput($event.target.value)',
    '(change)': '_onChange()',
    '(blur)': '_onTouched()',
    '(keydown)': '_onKeydown($event)',
  },
  exportAs: 'mdDatepickerInput, matDatepickerInput',
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: ElementRef, },
{type: Renderer2, },
{type: DateAdapter, decorators: [{ type: Optional }, ]},
{type: undefined, decorators: [{ type: Optional }, { type: Inject, args: [MD_DATE_FORMATS, ] }, ]},
{type: MdFormField, decorators: [{ type: Optional }, ]},
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'mdDatepicker': [{ type: Input },],
'matDatepicker': [{ type: Input },],
'mdDatepickerFilter': [{ type: Input },],
'matDatepickerFilter': [{ type: Input },],
'value': [{ type: Input },],
'min': [{ type: Input },],
'max': [{ type: Input },],
'disabled': [{ type: Input },],
'dateChange': [{ type: Output },],
'dateInput': [{ type: Output },],
};
}

function MdDatepickerInput_tsickle_Closure_declarations() {
/** @type {?} */
MdDatepickerInput.decorators;
/**
 * @nocollapse
 * @type {?}
 */
MdDatepickerInput.ctorParameters;
/** @type {?} */
MdDatepickerInput.propDecorators;
/** @type {?} */
MdDatepickerInput.prototype._datepicker;
/** @type {?} */
MdDatepickerInput.prototype._dateFilter;
/** @type {?} */
MdDatepickerInput.prototype._value;
/** @type {?} */
MdDatepickerInput.prototype._min;
/** @type {?} */
MdDatepickerInput.prototype._max;
/** @type {?} */
MdDatepickerInput.prototype._disabled;
/**
 * Emits when a `change` event is fired on this `<input>`.
 * @type {?}
 */
MdDatepickerInput.prototype.dateChange;
/**
 * Emits when an `input` event is fired on this `<input>`.
 * @type {?}
 */
MdDatepickerInput.prototype.dateInput;
/**
 * Emits when the value changes (either due to user input or programmatic change).
 * @type {?}
 */
MdDatepickerInput.prototype._valueChange;
/**
 * Emits when the disabled state has changed
 * @type {?}
 */
MdDatepickerInput.prototype._disabledChange;
/** @type {?} */
MdDatepickerInput.prototype._onTouched;
/** @type {?} */
MdDatepickerInput.prototype._cvaOnChange;
/** @type {?} */
MdDatepickerInput.prototype._validatorOnChange;
/** @type {?} */
MdDatepickerInput.prototype._datepickerSubscription;
/** @type {?} */
MdDatepickerInput.prototype._localeSubscription;
/**
 * The form control validator for whether the input parses.
 * @type {?}
 */
MdDatepickerInput.prototype._parseValidator;
/**
 * The form control validator for the min date.
 * @type {?}
 */
MdDatepickerInput.prototype._minValidator;
/**
 * The form control validator for the max date.
 * @type {?}
 */
MdDatepickerInput.prototype._maxValidator;
/**
 * The form control validator for the date filter.
 * @type {?}
 */
MdDatepickerInput.prototype._filterValidator;
/**
 * The combined form control validator for this input.
 * @type {?}
 */
MdDatepickerInput.prototype._validator;
/**
 * Whether the last value set on the input was valid.
 * @type {?}
 */
MdDatepickerInput.prototype._lastValueValid;
/** @type {?} */
MdDatepickerInput.prototype._elementRef;
/** @type {?} */
MdDatepickerInput.prototype._renderer;
/** @type {?} */
MdDatepickerInput.prototype._dateAdapter;
/** @type {?} */
MdDatepickerInput.prototype._dateFormats;
/** @type {?} */
MdDatepickerInput.prototype._mdFormField;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
